import { AssetConfig } from "./AssetConfig";
import { MatchDetector } from "./MatchDetector";
import type { Cell, GameOptions, MatchGroup, Swap, TileData } from "./types";

export interface FallMove {
  from: Cell;
  to: Cell;
}

export interface NewTileEntry {
  cell: Cell;
  tile: TileData;
}

export interface CollapseResult {
  removedCells: Cell[];
  fallMoves: FallMove[];
  newTiles: NewTileEntry[];
}

/** Default options: 8x8, 5 persons per level, 3+ matches. */
const DEFAULT_OPTS: GameOptions = {
  rows: 8,
  cols: 8,
  personsPerLevel: 5,
  minMatchLength: 3,
};

/**
 * Pure-data board for a match-3 game. Knows nothing about Phaser — the scene
 * reads its state and plays animations around the diffs it reports.
 *
 * Responsibilities:
 *   - Generate a no-initial-match board with a valid swap guaranteed.
 *   - Apply swaps; report whether a swap produces a match (without mutating).
 *   - Collapse matches, fall tiles, spawn new tiles from the active person pool.
 *   - Detect the no-valid-swap deadlock condition and shuffle when stuck.
 */
export class BoardLogic {
  readonly rows: number;
  readonly cols: number;
  readonly minMatchLength: number;
  readonly personsPerLevel: number;

  /** Plain 2D grid, mutable. `null` = empty cell. */
  board: (TileData | null)[][] = [];
  /** Person ids active for this level. */
  private activePersonIds: string[] = [];

  constructor(
    private readonly assetConfig: AssetConfig,
    options: Partial<GameOptions> = {},
  ) {
    const opts = { ...DEFAULT_OPTS, ...options };
    this.rows = opts.rows;
    this.cols = opts.cols;
    this.minMatchLength = opts.minMatchLength;
    this.personsPerLevel = opts.personsPerLevel;
  }

  // -------------------------------------------------------------------------
  // Read
  // -------------------------------------------------------------------------

  get(row: number, col: number): TileData | null {
    return this.board[row]?.[col] ?? null;
  }

  getActivePersonIds(): string[] {
    return this.activePersonIds.slice();
  }

  // -------------------------------------------------------------------------
  // Initialization
  // -------------------------------------------------------------------------

  /** Build a fresh board: no pre-existing matches, at least one valid swap. */
  initialize(): void {
    this.activePersonIds = this.choosePersons(this.personsPerLevel);
    const maxAttempts = 50;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      this.generateBoard();
      if (
        MatchDetector.detect(this.board, this.minMatchLength).length === 0 &&
        this.hasValidSwap()
      ) {
        return;
      }
    }
    // Fallback: still set the board so the game can play; if deadlocked the
    // scene will trigger a shuffle on the next animation tick.
    this.generateBoard();
  }

  /** Pick `count` distinct persons at random from the available pool. */
  private choosePersons(count: number): string[] {
    const pool = this.assetConfig.listPersonIds();
    const picked: string[] = [];
    const remaining = pool.slice();
    const take = Math.min(count, remaining.length);
    for (let i = 0; i < take; i++) {
      const idx = Math.floor(Math.random() * remaining.length);
      picked.push(remaining.splice(idx, 1)[0]);
    }
    return picked;
  }

  /** Place tiles row-by-row, skipping any tile that would create a match. */
  private generateBoard(): void {
    this.board = Array.from({ length: this.rows }, () =>
      Array<TileData | null>(this.cols).fill(null),
    );
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        let attempts = 0;
        let tile: TileData;
        do {
          tile = this.assetConfig.pickPhoto(this.activePersonIds);
          attempts++;
        } while (
          attempts < 200 &&
          this.wouldCreateMatch(r, c, tile.personId)
        );
        this.board[r][c] = tile;
      }
    }
  }

  /**
   * Would placing personId at (r, c) (over an empty cell) form a min-length
   * run with the already-placed neighbours to the left and above?
   */
  private wouldCreateMatch(r: number, c: number, personId: string): boolean {
    let leftCount = 0;
    for (let cc = c - 1; cc >= 0; cc--) {
      const tile = this.board[r][cc];
      if (!tile || tile.personId !== personId) break;
      leftCount++;
    }
    if (leftCount >= this.minMatchLength - 1) return true;

    let upCount = 0;
    for (let rr = r - 1; rr >= 0; rr--) {
      const tile = this.board[rr][c];
      if (!tile || tile.personId !== personId) break;
      upCount++;
    }
    if (upCount >= this.minMatchLength - 1) return true;

    return false;
  }

  // -------------------------------------------------------------------------
  // Swap
  // -------------------------------------------------------------------------

  /** Are two cells orthogonally adjacent? */
  static isAdjacent(a: Cell, b: Cell): boolean {
    const dr = Math.abs(a.row - b.row);
    const dc = Math.abs(a.col - b.col);
    return (dr === 1 && dc === 0) || (dr === 0 && dc === 1);
  }

  /** Swap two tiles in place. Caller must ensure adjacency. */
  swap(a: Cell, b: Cell): void {
    const tmp = this.board[a.row][a.col];
    this.board[a.row][a.col] = this.board[b.row][b.col];
    this.board[b.row][b.col] = tmp;
  }

  /** Would swapping (a, b) produce a match anywhere on the board? */
  wouldSwapMatch(a: Cell, b: Cell): boolean {
    if (!BoardLogic.isAdjacent(a, b)) return false;
    if (!this.board[a.row][a.col] || !this.board[b.row][b.col]) return false;
    this.swap(a, b);
    const matched =
      MatchDetector.detect(this.board, this.minMatchLength).length > 0;
    this.swap(a, b); // undo
    return matched;
  }

  // -------------------------------------------------------------------------
  // Match detection & collapse
  // -------------------------------------------------------------------------

  findMatches(): MatchGroup[] {
    return MatchDetector.detect(this.board, this.minMatchLength);
  }

  /**
   * Remove matched cells, drop surviving tiles downward, spawn new tiles at
   * the top of each column. Returns enough info for the scene to animate:
   *   - removedCells: which cells became empty
   *   - fallMoves: which surviving tiles moved from where to where
   *   - newTiles: which cells got freshly-spawned tiles
   */
  collapse(matches: MatchGroup[]): CollapseResult {
    const removedSet = new Set<string>();
    for (const g of matches) {
      for (const cell of g.cells) removedSet.add(`${cell.row},${cell.col}`);
    }

    const removedCells: Cell[] = [];
    const fallMoves: FallMove[] = [];
    const newTiles: NewTileEntry[] = [];

    for (let col = 0; col < this.cols; col++) {
      // Collect survivors in this column, bottom-up, keeping their original row.
      const survivors: { tile: TileData; originalRow: number }[] = [];
      for (let row = this.rows - 1; row >= 0; row--) {
        const tile = this.board[row][col];
        if (tile && !removedSet.has(`${row},${col}`)) {
          survivors.push({ tile, originalRow: row });
        }
      }

      // Clear the entire column in one pass.
      for (let row = 0; row < this.rows; row++) this.board[row][col] = null;

      // Place survivors at the bottom.
      for (let i = 0; i < survivors.length; i++) {
        const destRow = this.rows - 1 - i;
        const dest: Cell = { row: destRow, col };
        const { tile, originalRow } = survivors[i];
        if (originalRow !== destRow) {
          fallMoves.push({ from: { row: originalRow, col }, to: dest });
        }
        this.board[destRow][col] = tile;
      }

      // Spawn new tiles above.
      const gap = this.rows - survivors.length;
      for (let i = 0; i < gap; i++) {
        const destRow = gap - 1 - i;
        const tile = this.assetConfig.pickPhoto(this.activePersonIds);
        this.board[destRow][col] = tile;
        newTiles.push({ cell: { row: destRow, col }, tile });
      }
    }

    for (const key of removedSet) {
      const [r, c] = key.split(",").map(Number);
      removedCells.push({ row: r, col: c });
    }
    return { removedCells, fallMoves, newTiles };
  }

  // -------------------------------------------------------------------------
  // Deadlock / shuffle
  // -------------------------------------------------------------------------

  /** Is there at least one orthogonal swap that produces a match? */
  hasValidSwap(): boolean {
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const here: Cell = { row: r, col: c };
        const right: Cell = { row: r, col: c + 1 };
        const down: Cell = { row: r + 1, col: c };
        if (c + 1 < this.cols && this.wouldSwapMatch(here, right)) return true;
        if (r + 1 < this.rows && this.wouldSwapMatch(here, down)) return true;
      }
    }
    return false;
  }

  /** One valid swap to use as a hint / a place to break a deadlock visually. */
  findOneValidSwap(): Swap | null {
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const here: Cell = { row: r, col: c };
        const right: Cell = { row: r, col: c + 1 };
        const down: Cell = { row: r + 1, col: c };
        if (c + 1 < this.cols && this.wouldSwapMatch(here, right)) return [here, right];
        if (r + 1 < this.rows && this.wouldSwapMatch(here, down)) return [here, down];
      }
    }
    return null;
  }

  /** Re-generate the board using the same no-initial-match loop. */
  shuffle(): void {
    this.initialize();
  }
}
